import * as React from "react";
import type { ReactElement } from "react";
import { Tooltip as TooltipRadix } from "radix-ui";
import styles from "./Tooltip.css";

type TooltipProps = {
  content: string;
  /** The trigger — it must forward its ref (e.g. `IconButton`). */
  children: ReactElement;
};

/**
 * A short hover/focus label for a trigger, typically an icon-only button.
 *
 * Carries its own Provider so it works anywhere without app-level setup.
 * Forwards its ref and any extra props to the trigger, so it can itself sit
 * inside another Radix `asChild` trigger (e.g. `DropdownMenu.Trigger`).
 */
const Tooltip = React.forwardRef<HTMLButtonElement, TooltipProps>(
  ({ content, children, ...triggerProps }, ref) => (
    <TooltipRadix.Provider delayDuration={300}>
      <TooltipRadix.Root>
        <TooltipRadix.Trigger {...triggerProps} ref={ref} asChild>
          {children}
        </TooltipRadix.Trigger>
        <TooltipRadix.Portal>
          <TooltipRadix.Content
            className={styles.content}
            sideOffset={4}
            collisionPadding={8}
          >
            {content}
          </TooltipRadix.Content>
        </TooltipRadix.Portal>
      </TooltipRadix.Root>
    </TooltipRadix.Provider>
  ),
);

export default Tooltip;
